import styled from "@emotion/styled";
import {
  color,
  border,
  space,
  typography,
  variant,
  flexbox,
  layout,
  position,
} from "styled-system";

import { lightBlue, white, teal, gray, black } from "./colors";

const baseButtonProps = {
  boxShadow: "none",
  border: "none",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  textTransform: "uppercase",
  padding: "0 72px",
  height: "47px",
  textDecoration: "none",
  justifyContent: "center",
  width: ["100%", "unset", "unset"],
  transition: "all .5s ease",
  backgroundColor: "transparent",
  fontWeight: 700,
};

const underlineHover = {
  "&::after": {
    content: '""',
    position: "absolute",
    width: "100%",
    transform: "scaleX(0)",
    height: "2px",
    bottom: "-2px",
    left: 0,
    backgroundColor: black,
    transformOrigin: "bottom right",
    transition: "transform 0.25s ease-out",
  },
  "&:hover::after": {
    transform: "scaleX(1)",
    transformOrigin: "bottom left",
  },
};

const baseNavigationProps = {
  textTransform: "uppercase",
  fontWeight: 700,
  fontSize: 16,
  textDecoration: "none",
  position: "relative",
  backgroundColor: "transparent",
  boxShadow: "none",
  border: "none",
  padding: 0,
};

const baseSubnav = {
  backgroundColor: "transparent",
  boxShadow: "none",
  border: "none",
  padding: 0,
  fontSize: "16px",
  textDecoration: "none",
  marginLeft: [0, 0, 30],
  marginBottom: [18, 18, 0],
};

const variants = {
  hcp: {},
  navigationWhite: {
    ...baseNavigationProps,
    color: white,
    ...underlineHover,
    "&::after": {
      ...underlineHover["&::after"],
      backgroundColor: white,
    },
  },
  navigationWhiteNoHover: {
    ...baseNavigationProps,
    color: white,
  },
  navigationBlueNoHover: {
    ...baseNavigationProps,
    color: lightBlue,
  },
  navigationPatientMain: {
    ...baseNavigationProps,
    color: [lightBlue, lightBlue, white],
    ...underlineHover,
    "&::after": {
      ...underlineHover["&::after"],
      backgroundColor: [lightBlue, lightBlue, white],
    },
  },
  navigationWhiteLight: {
    ...baseNavigationProps,
    ...underlineHover,
    color: white,
    fontWeight: 400,
    "&::after": {
      ...underlineHover["&::after"],
      height: "2px",
      bottom: "-2px",
      backgroundColor: white,
    },
  },
  navigationBlueLight: {
    ...baseNavigationProps,
    ...underlineHover,
    fontWeight: 400,
    color: lightBlue,
    "&::after": {
      ...underlineHover["&::after"],
      height: "2px",
      bottom: "-2px",
      backgroundColor: lightBlue,
    },
  },
  navigationBlack: {
    ...baseNavigationProps,
    ...underlineHover,
  },
  navigationBlue: {
    ...baseNavigationProps,
    color: lightBlue,
    ...underlineHover,
    "&::after": {
      ...underlineHover["&::after"],
      backgroundColor: lightBlue,
    },
  },
  navigationTeal: {
    ...baseNavigationProps,
    color: teal,
    ...underlineHover,
    fontFamily: "Quicksand",

    "&::after": {
      ...underlineHover["&::after"],
      backgroundColor: teal,
    },
  },
  patientSitemap: {
    ...baseNavigationProps,
    color: teal,
    ...underlineHover,
    fontFamily: "Quicksand",
    fontSize: "22px",
    marginBottom: "18px",
    width: "fit-content",
    "&::after": {
      ...underlineHover["&::after"],
      backgroundColor: teal,
    },
  },
  hcpSitemap: {
    ...baseNavigationProps,
    color: black,
    ...underlineHover,
    fontFamily: "Mulish",
    fontSize: "22px",
    marginBottom: "18px",
    width: "fit-content",
    "&::after": {
      ...underlineHover["&::after"],
      backgroundColor: black,
    },
  },
  hcpNavigation: {
    ...baseNavigationProps,
    "&::after": {
      content: '""',
      position: "absolute",
      width: "100%",
      transform: "scaleX(0)",
      height: "2px",
      bottom: "-2px",
      left: 0,
      backgroundColor: [white, white, lightBlue],
      transformOrigin: "bottom right",
      transition: "transform 0.25s ease-out",
    },
    "&:hover::after": {
      transform: "scaleX(1)",
      transformOrigin: "bottom left",
    },
    color: lightBlue,
    marginLeft: "46px",
  },
  hcpNavigationFooter: {
    ...baseNavigationProps,
    "&::after": {
      content: '""',
      position: "absolute",
      width: "100%",
      transform: "scaleX(0)",
      height: "2px",
      bottom: "-2px",
      left: 0,
      backgroundColor: [lightBlue, lightBlue, lightBlue],
      transformOrigin: "bottom right",
      transition: "transform 0.25s ease-out",
    },
    "&:hover::after": {
      transform: "scaleX(1)",
      transformOrigin: "bottom left",
    },
    color: lightBlue,
    marginLeft: "46px",
  },
  patientSubnav: {
    ...baseSubnav,
    fontSize: "13px",
    color: [lightBlue, lightBlue, white],
  },
  patientSubnavEsp: {
    ...baseSubnav,
    fontSize: "12px",
    marginLeft: [0, 0, 15],
    color: [lightBlue, lightBlue, white],
  },
  hcpSubnav: {
    ...baseSubnav,
    color: white,
  },
  footer: {
    ...baseNavigationProps,
    color: white,
    margin: "0 14px 10px 0",
    marginLeft: [0, 0, 0],
    ...underlineHover,
    "&::after": {
      ...underlineHover["&::after"],
      backgroundColor: white,
    },
  },
  sitemap: {
    ...baseNavigationProps,
    color: teal,
    marginBottom: "18px",
    fontSize: "22px",
    marginLeft: 0,
  },
  subfooter: {
    textDecoration: "none",
    fontSize: 15,
    "&::after": {
      content: '"|"',
      margin: "0 5px",
    },
    "&:last-of-type": {
      "&::after": {
        content: '""',
        margin: "0",
      },
    },
  },
  button: {
    ...baseButtonProps,
    color: lightBlue,
    border: `2px solid ${lightBlue}`,
    "&:hover": {
      backgroundColor: lightBlue,
      color: white,
    },
  },
  whiteButton: {
    ...baseButtonProps,
    color: white,
    border: `2px solid ${white}`,
    "&:hover": {
      backgroundColor: white,
      color: lightBlue,
    },
  },
  solidButtonTeal: {
    ...baseButtonProps,
    textAlign: "center",
    backgroundColor: teal,
    border: `2px solid ${teal}`,
    color: white,
    height: "unset",
    padding: "18px 30px",
    "&:hover": {
      backgroundColor: white,
      color: teal,
    },
  },
  solidButtonLightBlue: {
    ...baseButtonProps,
    textAlign: "center",
    backgroundColor: lightBlue,
    color: white,
    height: "unset",
    border: `2px solid ${lightBlue}`,
    padding: "18px 30px",
    "&:hover": {
      backgroundColor: white,
      color: lightBlue,
      border: `2px solid ${lightBlue}`,
    },
  },
  disabledSolidButton: {
    ...baseButtonProps,
    textAlign: "center",
    backgroundColor: gray,
    border: `2px solid ${gray}`,
    color: white,
    height: "unset",
    padding: "18px 30px",
    cursor: "not-allowed",
  },
  basicUnderlineHover: {
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
};

const Link = styled.a`
  color: inherit;
  font-weight: inherit;
  font-family: inherit;

  ${variant({ variants })}
  ${typography}
  ${space}
  ${flexbox}
  ${color}
  ${border}
  ${layout}
  ${position}
`;

export default Link;
